import React from 'react';
import DashboardLayout from '../../components/dashboard/DashboardLayout';

const TrickAndTrapQuestions = () => {
  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold mb-2">Trick and Trap Questions 💭</h1>
        <p className="text-gray-600 text-lg mb-12">Why it's a trap and how to convey the best answer.</p>

        <div className="space-y-16">

            {/* Question 1 */}
            <div>
            <h2 className="text-2xl font-semibold mb-4">1. Tell me about yourself</h2>
            
            <div className="bg-red-50 p-4 rounded mb-6">
              <p className="text-gray-800">⚠️ <span className="font-medium">TRAP:</span> Beware; about 80% of all interviews begin with this "innocent" question. Many candidates, unprepared for the question, skewer themselves by rambling, recapping their life story, delving into ancient work history or personal matters.</p>
            </div>
            
            <div className="space-y-4 text-gray-800">
              <p>Start with the present and tell why you are well qualified for the position. Remember that the key to all successful interviewing is to match your qualifications to what the interviewer is looking for. In other words you must sell what the buyer is buying. This is the single most important strategy in job hunting.</p>
              
              <p>So, before you answer this or any question it's imperative that you try to uncover your interviewer's greatest need, want, problem or goal.</p>

              <div className="pl-4 border-l-2 border-gray-200">
                <p className="mb-2">To do so, make sure you take these two steps:</p>
                <ol className="list-decimal pl-6 space-y-2">
                  <li>Do all the homework you can before the interview to uncover this person's wants and needs (not the generalized needs of the industry or company)</li>
                  <li>As early as you can in the interview, ask for a more complete description of what the position entails. You might say:</li>
                </ol>
                <div className="bg-gray-50 p-4 rounded my-4">
                  <p className="text-gray-700">"I have a number of accomplishments I'd like to tell you about, but I want to make the best use of our time together and talk directly to your needs. To help me do that, could you tell me more about the most important priorities of this position? All I know is what I (heard from the recruiter, read in the classified ad, etc.)"</p>
                </div>
              </div>

              <div className="bg-yellow-50 p-4 rounded">
                <p className="font-medium mb-2">💡 Pro Tip:</p>
                <p>ALWAYS follow-up with a second and possibly, third question, to draw out needs even more. You might ask simply:</p>
                <ul className="list-disc pl-6 mt-2">
                  <li>"And in addition to that?..."</li>
                  <li>"Is there anything else you see as essential to success in this position?"</li>
                </ul>
              </div>

              <p>After uncovering what the employer is looking for, describe why the needs of this job bear striking parallels to tasks you've succeeded at before. Be sure to illustrate with specific examples of your responsibilities and especially your achievements, all of which are geared to present yourself as a perfect match for the needs they have just described.</p>
            </div>
          </div>

          {/* Question 2 */}
          <div>
            <h2 className="text-2xl font-semibold mb-4">2. Tell me about something you did — or failed to do — that you now feel a little ashamed of</h2>
            
            <div className="bg-red-50 p-4 rounded mb-6">
              <p className="text-gray-800">⚠️ <span className="font-medium">TRAP:</span> There are some questions your interviewer has no business asking, and this is one. Some interviewers ask this question on the chance you admit to something, but if not, at least they'll see how you think on your feet.</p>
            </div>

            <div className="space-y-4 text-gray-800">
              <p>As with faults and weaknesses, never confess a regret. But don't seem as if you're stonewalling either.</p>

              <div className="bg-blue-50 p-4 rounded">
                <p className="font-medium mb-2">🎯 Best Strategy:</p>
                <p>Say you harbor no regrets, then add a principle or habit you practice regularly for healthy human relations.</p>
              </div>

              <div className="pl-4 border-l-2 border-gray-200">
                <p className="font-medium mb-2">Example Response:</p>
                <p className="mb-4">Pause for reflection, as if the question never occurred to you. Then say:</p>
                <div className="bg-gray-50 p-4 rounded space-y-4">
                  <p>"You know, I really can't think of anything."</p>
                  <p>"I would add that as a general management principle, I've found that the best way to avoid regrets is to avoid causing them in the first place. I practice one habit that helps me a great deal in this regard. At the end of each day, I mentally review the day's events and conversations to take a second look at the people and developments I'm involved with and do a doublecheck of what they're likely to be feeling. Sometimes I'll see things that do need more follow-up, whether a pat on the back, or maybe a five minute chat in someone's office to make sure we're clear on things...whatever."</p>
                </div>
              </div>
            </div>
          </div>

          {/* Question 3 */}
          <div>
            <h2 className="text-2xl font-semibold mb-4">3. Why are you leaving (or did you leave) this position?</h2>
            
            <div className="bg-red-50 p-4 rounded mb-6">
              <p className="text-gray-800">⚠️ <span className="font-medium">TRAP:</span> Never badmouth your previous industry, company, board, boss, staff, employees or customers. This rule is inviolable: never be negative. Any mud you hurl will only soil your suit.</p>
            </div>

            <div className="space-y-6 text-gray-800">
              <div className="pl-4 border-l-2 border-gray-200">
                <h3 className="font-medium mb-2">If you have a job presently:</h3>
                <p>If you're not yet 100% committed to leaving your present post, don't be afraid to say so. Since you have a job, you are in a stronger position than someone who does not. But don't be coy either. State honestly what you'd be hoping to find in a new spot.</p>
              </div>

              <div className="pl-4 border-l-2 border-gray-200">
                <h3 className="font-medium mb-2">If you do not presently have a job:</h3>
                <p className="mb-4">Never lie about having been fired. It's unethical -- and too easily checked. But do try to deflect the reason from you personally. If your firing was the result of a takeover, merger, division wide layoff, etc., so much the better.</p>
                <div className="bg-yellow-50 p-4 rounded">
                  <p className="mb-2">💡 Professional Approach:</p>
                  <p>Describe your own firing -- candidly, succinctly and without a trace of bitterness -- from the company's point-of-view, indicating that you could understand why it happened and you might have made the same decision yourself.</p>
                </div>
              </div>

              <div className="pl-4 border-l-2 border-gray-200">
                <h3 className="font-medium mb-2">For all prior positions:</h3>
                <p>Make sure you've prepared a brief reason for leaving.</p>
                <div className="bg-green-50 p-4 rounded mt-2">
                  <p className="font-medium">✅ Best reasons:</p>
                  <ul className="list-disc pl-6 mt-2">
                    <li>More money</li>
                    <li>Opportunity</li>
                    <li>Responsibility</li>
                    <li>Growth</li>
                  </ul>
                </div>
              </div>
            </div>
        </div>

          {/* Question 4 */}
          <div>
            <h2 className="text-2xl font-semibold mb-4">4. The "Silent Treatment"</h2>
            
            <div className="bg-red-50 p-4 rounded mb-6">
              <p className="text-gray-800">⚠️ <span className="font-medium">TRAP:</span> Beware -- if you are unprepared for this question, you will probably not handle it right and possibly blow the interview. Here's how it works:</p>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li>You answer an interviewer's question and then, instead of asking another, they just stare at you in a deafening silence.</li>
                <li>You wait, growing a bit uneasy, and there they sit, silent as Mt. Rushmore, as if they don't believe what you've just said.</li>
                <li>Most unprepared candidates rush in to fill the void of silence, rambling on and showing how flustered they are.</li>
              </ul>
            </div>

            <div className="space-y-4 text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p className="font-medium mb-2">🎯 Best Strategy:</p>
                <p>Like a primitive tribal mask, the Silent Treatment loses all its power to frighten you once you refuse to be intimidated. If your interviewer pulls it, keep quiet yourself for a while and then ask, with sincere politeness and not a trace of sarcasm:</p>
                <div className="bg-white p-4 rounded mt-2">
                  <p className="italic">"Is there anything else I can fill in on that point?"</p>
                </div>
              </div>
              
              <div className="bg-yellow-50 p-4 rounded">
                <p className="font-medium">💡 Important:</p>
                <p>Don't let the Silent Treatment intimidate you into talking a blue streak, because you could easily talk yourself out of the position.</p>
              </div>
            </div>
          </div>

          {/* Question 5 */}
          <div>
            <h2 className="text-2xl font-semibold mb-4">5. Aren't you overqualified for this position?</h2>
            
            <div className="bg-red-50 p-4 rounded mb-6">
              <p className="text-gray-800">⚠️ <span className="font-medium">TRAP:</span> The employer may be concerned that you'll grow dissatisfied and leave.</p>
            </div>

            <div className="space-y-4 text-gray-800">
              <p>As with any objection, don't view this as a sign of imminent defeat. It's an invitation to teach the interviewer a new way to think about this situation, seeing advantages instead of drawbacks.</p>

              <div className="pl-4 border-l-2 border-gray-200">
                <p className="font-medium mb-2">Example Response:</p>
                <div className="bg-gray-50 p-4 rounded space-y-4">
                  <p>"I recognize the job market for what it is -- a marketplace. Like any marketplace, it's subject to the laws of supply and demand. So 'overqualified' can be a relative term, depending on how tight the job market is. And right now, it's very tight. I understand and accept that."</p>
                  <p>"I also believe that there could be very positive benefits for both of us in this match."</p>
                  <p>"Because of my unusually strong experience in _________________, I could start to contribute right away, perhaps much faster than someone who'd have to be brought along more slowly."</p>
                  <p>"There's also the value of all the training and years of experience that other companies have invested tens of thousands of dollars to give me. You'd be getting all the value of that without having to pay an extra dime for it. With someone who has yet to acquire that experience, he'd have to gain it on your nickel."</p>
                </div>
              </div>

              <div className="bg-yellow-50 p-4 rounded">
                <p className="font-medium mb-2">💡 Key Point:</p>
                <p>The main concern behind the "overqualified" question is that you will leave your new employer as soon as something better comes your way. Anything you can say to demonstrate the sincerity of your commitment to the employer and reassure them that you're looking to stay for the long-term will help you overcome this objection.</p>
              </div>
            </div>
          </div>

          {/* Question 6 */}
          <div>
            <h2 className="text-2xl font-semibold mb-4">6. Where do you see yourself five years from now?</h2>
            
            <div className="bg-red-50 p-4 rounded mb-6">
              <p className="text-gray-800">⚠️ <span className="font-medium">TRAP:</span> One reason interviewers ask this question is to see if you're settling for this position, using it merely as a stopover until something better comes along. Or they could be trying to gauge your level of ambition.</p>
              <p className="mt-2">If you're too specific, i.e., naming the promotions you someday hope to win, you'll sound presumptuous. If you're too vague, you'll seem rudderless.</p>
            </div>

            <div className="space-y-4 text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p className="font-medium mb-2">🎯 Best Answer:</p>
                <p>Reassure your interviewer that you're looking to make a long-term commitment...that this position entails exactly what you're looking to do and what you do extremely well. As for your future, you believe that if you perform each job at hand with excellence, future opportunities will take care of themselves.</p>
              </div>

              <div className="pl-4 border-l-2 border-gray-200">
                <p className="font-medium mb-2">Example Response:</p>
                <div className="bg-gray-50 p-4 rounded">
                  <p>"I am definitely interested in making a long-term commitment to my next position. Judging by what you've told me about this position, it's exactly what I'm looking for and what I am very well qualified to do. In terms of my future career path, I'm confident that if I do my work with excellence, opportunities will inevitably open up for me. It's always been that way in my career, and I'm confident I'll have similar opportunities here."</p>
                </div>
              </div>
            </div>
          </div>

          {/* Question 7 */}
          <div>
            <h2 className="text-2xl font-semibold mb-4">7. Describe your ideal company, location and job</h2>
            
            <div className="bg-red-50 p-4 rounded mb-6">
              <p className="text-gray-800">⚠️ <span className="font-medium">TRAP:</span> This is often asked by an experienced interviewer who thinks you may be overqualified, but knows better than to show their hand by posing their objection directly.</p>
            </div>

            <div className="space-y-4 text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p className="font-medium mb-2">🎯 Best Answer:</p>
                <p>The only right answer is to describe what this company is offering, being sure to make your answer believable with specific reasons, stated with sincerity, why each quality represented by this opportunity is attractive to you.</p>
              </div>

              <div className="bg-yellow-50 p-4 rounded">
                <p className="font-medium mb-2">💡 Important Note:</p>
                <p>Remember that if you're coming from a company that's the leader in its field or from a glamorous or much admired company, industry, city or position, your interviewer and his company may well have an "Avis" complex. That is, they may feel a bit defensive about being "second best" to the place you're coming from.</p>
              </div>

              <p>If you do not express genuine enthusiasm for the firm, its culture, location, industry, etc., you may fail to answer this "Avis" complex objection and, as a result, leave the interviewer suspecting that a hot shot like you, coming from a Fortune 500 company in New York, just wouldn't be happy at an unknown manufacturer based in Topeka, Kansas.</p>
            </div>
          </div>

          {/* Question 8 */}
          <div>
            <h2 className="text-2xl font-semibold mb-4">8. Why do you want to work at our company?</h2>
            
            <div className="bg-red-50 p-4 rounded mb-6">
              <p className="text-gray-800">⚠️ <span className="font-medium">TRAP:</span> This question tests whether you've done any homework about the firm. If you haven't, you lose. If you have, you win big.</p>
            </div>

            <div className="space-y-4 text-gray-800">
              <p>This question is your opportunity to hit the ball out of the park, thanks to the in-depth research you should do before any interview.</p>

              <div className="bg-blue-50 p-4 rounded">
                <p className="font-medium mb-2">📚 Best sources for research:</p>
                <ul className="list-disc pl-6">
                  <li>Annual reports</li>
                  <li>Corporate newsletter</li>
                  <li>Contacts you know at the company or its suppliers</li>
                  <li>Advertisements</li>
                  <li>Articles about the company in the trade press</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Question 9 */}
          <div>
            <h2 className="text-2xl font-semibold mb-4">9. Why have you been out of work so long?</h2>
            
            <div className="bg-red-50 p-4 rounded mb-6">
              <p className="text-gray-800">⚠️ <span className="font-medium">TRAP:</span> A tough question if you've been on the beach a long time. You don't want to seem like damaged goods.</p>
            </div>

            <div className="space-y-4 text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p className="font-medium mb-2">🎯 Best Answer:</p>
                <p>You want to emphasize factors which have prolonged your job search by your own choice.</p>
              </div>

              <div className="pl-4 border-l-2 border-gray-200">
                <p className="font-medium mb-2">Example Response:</p>
                <div className="bg-gray-50 p-4 rounded">
                  <p>"After my job was terminated, I made a conscious decision not to jump on the first opportunities to come along. In my life, I've found out that you can always turn a negative into a positive IF you try hard enough. This is what I determined to do. I decided to take whatever time I needed to think through what I do best, what I most want to do, where I'd like to do it...and then identify those companies that could offer such an opportunity."</p>
                  <p className="mt-4">"Also, in all honesty, you have to factor in the recession (consolidation, stabilization, etc.) in the (banking, financial services, manufacturing, advertising, etc.) industry."</p>
                  <p className="mt-4">"So between my being selective and the companies in our industry downsizing, the process has taken time. But in the end, I'm convinced that when I do find the right match, all that careful evaluation from both sides of the desk will have been well worthwhile for both the company that hires me and myself."</p>
                </div>
              </div>
            </div>
          </div>

          {/* Question 10 */}
          <div>
            <h2 className="text-2xl font-semibold mb-4">10. Tell me about the most boring job you've ever had</h2>
            
            <div className="bg-red-50 p-4 rounded mb-6">
              <p className="text-gray-800">⚠️ <span className="font-medium">TRAP:</span> You give a very memorable description of a very boring job. Result? You become associated with this boring job in the interviewer's mind.</p>
            </div>

            <div className="space-y-4 text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p className="font-medium mb-2">🎯 Best Answer:</p>
                <p>You have never allowed yourself to grow bored with a job and you can't understand it when others let themselves fall into that rut.</p>
              </div>

              <div className="pl-4 border-l-2 border-gray-200">
                <p className="font-medium mb-2">Example Response:</p>
                <div className="bg-gray-50 p-4 rounded">
                  <p>"Perhaps I've been fortunate, but that I've never found myself bored with any job I have ever held. I've always enjoyed hard work. As with actors who feel there are no small parts, I also believe that in every company or department there are exciting challenges and intriguing problems crying out for energetic and enthusiastic solutions. If you're bored, it's probably because you're not challenging yourself to tackle those problems right under your nose."</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
    </DashboardLayout>
  );
};

export default TrickAndTrapQuestions;